import React from 'react';
import {
  Type,
  Table,
  TableRow,
  TableCell,
  TableThumbnail,
} from '@spotify-internal/encore-creator-web';
import { spacer8 } from '@spotify-internal/encore-foundation';
import { screenMdMin, screenMdMax } from '@spotify-internal/encore-foundation';
import styled from 'styled-components';
import { useMe } from '../../../hooks/useMe';
import { MeShows, Episode } from '../../../types';
import * as api from '../../../lib/Api';
import { Link as RouterLink } from 'react-router-dom';
import { rankAndSortEpisodesByShow } from '../rankAndSortUserEpisodes';
import { formatByOrderOfMagnitude } from '../../../lib/Formatter';
import moment, { Moment } from 'moment';
import { FormattedMessage } from 'react-intl';

type Props = {
  networkId: string | null;
  shows: MeShows[] | null;
};

export function TopEpisodes(props: Props) {
  const me = useMe();
  const dataAvailableUntil = me!.dataRange.until;
  const [topEpisodes, setTopEpisodes] = React.useState<
    Pick<Episode, 'id' | 'name' | 'artworkUrl' | 'starts'>[]
  >();

  const dataRange = React.useMemo(() => {
    return {
      from: moment(dataAvailableUntil)
        .subtract(6, 'days')
        .format('YYYY-MM-DD'),
      until: dataAvailableUntil,
    };
  }, [dataAvailableUntil]);

  React.useEffect(() => {
    async function getData() {
      try {
        if (props.networkId) {
          const topEpisodes = await api.getTopEpisodesForNetwork(props.networkId!, dataRange);
          setTopEpisodes(topEpisodes);
        } else if (props.shows && props.shows.length) {
          const episodesByShow = await Promise.all(
            props.shows.map(show => api.getTopEpisodesForPodcast(show.id, dataRange)),
          );

          setTopEpisodes(rankAndSortEpisodesByShow(episodesByShow));
        }
      } catch {
        setTopEpisodes([]);
      }
    }

    getData();
  }, [props.networkId, dataRange, props.shows]);

  const formatDate = (date: string | Moment) => moment(date).format('MMM DD');

  if (!topEpisodes || !topEpisodes.length) return null;

  return (
    <div>
      <HeadingContainer>
        <Heading>
          <FormattedMessage
            id="home.top-episodes-title"
            defaultMessage="Top episodes"
            description="Title for list of episodes with the most starts this week"
          />
        </Heading>
        <Type variant={Type.cta3} color="#616467" condensed style={{ textAlign: 'right' }}>
          {formatDate(moment(dataAvailableUntil).subtract(6, 'days'))} –{' '}
          {formatDate(dataAvailableUntil)}{' '}
          <FormattedMessage id="home.top-episodes-starts" defaultMessage="STARTS" />
        </Type>
      </HeadingContainer>
      <Table>
        <tbody>
          {topEpisodes.slice(0, TopEpisodeLimit).map(episode => (
            <TableRow hover key={episode.id} data-testid="table-row">
              <TableCell truncate title={episode.name} style={{ paddingLeft: 0 }}>
                <Link to={`/episode/${episode.id}`} style={{ textDecoration: 'none' }}>
                  <TableThumbnail
                    small
                    img={episode.artworkUrl}
                    imgAlt={episode.name}
                    thumbnailTitle={episode.name}
                  />
                </Link>
              </TableCell>
              <TableCell align="right" numerical style={{ paddingRight: 0 }}>
                {formatByOrderOfMagnitude(episode.starts)}
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

const TopEpisodeLimit = 5;

const Heading = styled(Type).attrs({
  forwardedAs: 'h2',
  variant: Type.heading3,
})`
  font-size: 18px;
  padding: 0;
  margin-right: ${spacer8};
  min-width: 100px;
`;

const HeadingContainer = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  border-bottom: 1px solid #979797;
  padding-bottom: 17px;

  @media (min-width: ${screenMdMin}) and (max-width: ${screenMdMax}) {
    display: block;
  }
`;

const Link = styled(RouterLink)`
  white-space: pre-wrap;
`;
